import { useCallback, useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import {
  Account,
  AccountTypeEnum,
  useListAndSearchAllAccounts,
  useManualSyncRequired,
} from "@bn/api/apiv1";
import { AccountsGroup, ExtendedAccount } from "./types";

export const groupOrder: AccountTypeEnum[] = [
  "01_depot",
  "02_cash",
  "05_material_assets",
];

const extendAccount = (
  account: Account,
  syncRequiredById: Record<number, boolean>
): ExtendedAccount => ({
  ...account,
  syncRequired: syncRequiredById[account.id] ?? false,
  importType: account.bankConnectionId ? "bank" : "manual",
  lastUpdatedDate: account.lastSyncDate,
  lastChanged: account.timestamps?.updatedAt ?? account.timestamps?.createdAt,
});

export const useAccountsByGroup = ({ showHidden }: { showHidden: boolean }) => {
  const { data, ...rest } = useListAndSearchAllAccounts(
    {
      // @ts-ignore
      order: ["name desc"],
      type: ["01_depot", "02_cash", "05_material_assets"],
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const accountIds = useMemo(() => accounts.map((a) => a.id) ?? [], [accounts]);

  const { data: syncStatusList } = useManualSyncRequired(
    { accountId: accountIds },
    { enabled: accountIds.length > 0 }
  );

  const syncRequiredById = useMemo(
    () =>
      syncStatusList?.syncStatuses?.reduce(
        (acc, s) => {
          if (s.accountId != null) {
            acc[s.accountId] = s.manualSyncRequired ?? false;
          }
          return acc;
        },
        {} as Record<number, boolean>
      ) ?? {},
    [syncStatusList]
  );

  const parentHiddenStates = useMemo(
    () =>
      accounts.reduce(
        (acc, { id, isHidden }) => {
          acc[id] = isHidden;
          return acc;
        },
        {} as { [key: number]: boolean | undefined }
      ) ?? {},
    [accounts]
  );

  const checkIsHidden = useCallback(
    (account: Account) =>
      account.isHidden ||
      (account.depotIds?.length && parentHiddenStates[account.depotIds[0]]),
    [parentHiddenStates]
  );

  const allExtendedAccounts = useMemo(
    () => accounts.map((a) => extendAccount(a, syncRequiredById)),
    [accounts, syncRequiredById]
  );

  const hiddenAccountsCount = useMemo(
    () => allExtendedAccounts.filter((a) => checkIsHidden(a)).length,
    [allExtendedAccounts, checkIsHidden]
  );

  const extendedAccounts = useMemo(
    () =>
      showHidden
        ? allExtendedAccounts
        : allExtendedAccounts.filter((a) => !checkIsHidden(a)),
    [allExtendedAccounts, showHidden, checkIsHidden]
  );

  const refMapByDepotId = useMemo(() => {
    const entries = extendedAccounts.flatMap((ext) =>
      (ext.depotIds ?? []).map((depotId) => [depotId, ext] as const)
    );
    return Object.fromEntries(entries) as Record<number, ExtendedAccount>;
  }, [extendedAccounts]);

  const groupedAccounts = useMemo(() => {
    const grouped = extendedAccounts.reduce<AccountsGroup[]>((acc, ext) => {
      if (ext.depotIds?.length) {
        return acc;
      }
      const existing = acc.find((g) => g.title === ext.type);
      if (existing) {
        existing.accounts.push(ext);
      } else {
        acc.push({ title: ext.type, accounts: [ext] });
      }
      return acc;
    }, []);

    return grouped.sort(
      (a, b) => groupOrder.indexOf(a.title) - groupOrder.indexOf(b.title)
    );
  }, [extendedAccounts]);

  return {
    ...rest,
    accounts: extendedAccounts,
    groupedAccounts: groupedAccounts || [],
    refMapByDepotId,
    hiddenAccountsCount,
    groups: groupOrder.filter((group) =>
      groupedAccounts.some(({ title }) => title === group)
    ),
  };
};
